import React from "react";
import NextImage from "../../NextImage";
import { CategoryType, type SearchOverviewProps } from "./types";
import { ThemeSection } from "./SearchResult/ThemeSection";
import { StockSection } from "./SearchResult/StockSection";
import { NewsSection } from "./SearchResult/NewsSection";
import { CryptoCardItem, StockCardItem, NewsItem } from "./SearchResult/types";
import Translations from "@translate/components/Menu/MenuSearchModal/SearchOverview";
import { I18nUtil } from "@utils/i18n";
import { SearchOpenData } from "@features/api/openData";

const I18n = I18nUtil.createScoped(Translations);

// 加载状态组件
const LoadingState = () => (
  <div className="flex flex-col space-y-3 py-2">
    {[...Array(10)].map((_, index) => (
      <div key={index} className="flex items-center p-3 animate-pulse">
        <div className="w-8 h-8 mr-3 rounded-full bg-background_white_overlay5"></div>
        <div className="flex-grow">
          <div className="h-4 bg-background_white_overlay5 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-background_white_overlay5 rounded w-1/2"></div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * 搜索概览组件
 * 显示所有有结果的分类，每个分类按限制数量显示部分内容
 * 当分类总数超过限制时显示 "View All" 按钮
 */
export const SearchOverview: React.FC<SearchOverviewProps> = ({
  categories,
  onViewAll,
  onClose,
  isLoading,
  originType,
}) => {
  // 如果正在加载，显示加载状态
  if (isLoading) {
    return <LoadingState />;
  }
  // 如果没有任何搜索结果，显示空状态
  if (categories.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[440px] w-full py-8">
        {/* 图标容器 */}
        <div className="w-[86px] h-[86px] mb-1 relative">
          {/* 背景圆形 */}
          <NextImage
            src="/assets/images/searchEmpty.webp"
            alt="No results"
            width={86}
            height={86}
          />
        </div>
        {/* 文本 - 使用设计中指定的字体样式和颜色 */}
        <span className="body_l text-content_quaternary">
          {I18n.t("No search results yet")}
        </span>
      </div>
    );
  }

  // 渲染分类内容
  const renderCategoryContent = (category: any) => {
    const handleViewAll = () => {
      onViewAll(category.type, category.title);
    };
    switch (category.type) {
      case CategoryType.STOCK_THEMES:
      case CategoryType.CRYPTO_THEMES:
        return (
          <ThemeSection
            title={category.title}
            themes={category.data as SearchOpenData[]}
            type={
              category.type === CategoryType.STOCK_THEMES ? "stock" : "crypto"
            }
            viewAllText={category.showViewAll ? `View all` : undefined}
            onViewAll={category.showViewAll ? handleViewAll : undefined}
            onClose={onClose}
          />
        );

      case CategoryType.STOCKS:
        return (
          <StockSection
            title={category.title}
            stocks={category.data as StockCardItem[]}
            type={"stock"}
            originType={originType}
            viewAllText={category.showViewAll ? `View all` : undefined}
            onViewAll={category.showViewAll ? handleViewAll : undefined}
            onClose={onClose}
          />
        );

      case CategoryType.CRYPTO:
        return (
          <StockSection
            title={category.title}
            type={"crypto"}
            stocks={category.data as CryptoCardItem[]}
            originType={originType}
            viewAllText={category.showViewAll ? `View all` : undefined}
            onViewAll={category.showViewAll ? handleViewAll : undefined}
            onClose={onClose}
          />
        );

      case CategoryType.NEWS:
        return (
          <NewsSection
            title={category.title}
            news={category.data as NewsItem[]}
            viewAllText={category.showViewAll ? `View all` : undefined}
            onViewAll={category.showViewAll ? handleViewAll : undefined}
            onClose={onClose}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-8 px-6 py-4">
      {categories.map((category) => (
        <div key={category.type} className="flex flex-col">
          {renderCategoryContent(category)}
        </div>
      ))}
    </div>
  );
};
